import React, { useState, useEffect } from 'react';
import './PDFExportDialog.css';

interface PDFExportOptions {
  sections: string[];
  includeCharts: boolean;
  includeMetadata: boolean;
  includeRecommendations: boolean;
  includeNextSteps: boolean;
}

interface PDFExportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (options: PDFExportOptions) => void;
  availableSections: string[];
  queryResult: any;
}

const PDFExportDialog: React.FC<PDFExportDialogProps> = ({
  isOpen,
  onClose,
  onExport,
  availableSections,
  queryResult
}) => {
  const [selectedSections, setSelectedSections] = useState<string[]>([]);
  const [includeCharts, setIncludeCharts] = useState(true);
  const [includeMetadata, setIncludeMetadata] = useState(true);
  const [includeRecommendations, setIncludeRecommendations] = useState(true);
  const [includeNextSteps, setIncludeNextSteps] = useState(true);
  const [selectAll, setSelectAll] = useState(true);

  useEffect(() => {
    // Initialize with all sections selected
    if (availableSections.length > 0 && selectedSections.length === 0) {
      setSelectedSections(availableSections);
    }
  }, [availableSections]);

  if (!isOpen) return null;

  const handleSectionToggle = (section: string) => {
    if (selectedSections.includes(section)) {
      setSelectedSections(selectedSections.filter(s => s !== section));
      setSelectAll(false);
    } else {
      const newSections = [...selectedSections, section];
      setSelectedSections(newSections);
      setSelectAll(newSections.length === availableSections.length);
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedSections([]);
      setSelectAll(false);
    } else {
      setSelectedSections(availableSections);
      setSelectAll(true);
    }
  };

  const handleExport = () => {
    const options: PDFExportOptions = {
      sections: selectedSections,
      includeCharts,
      includeMetadata,
      includeRecommendations,
      includeNextSteps
    };
    onExport(options);
  };

  const getSectionDisplayName = (section: string): string => {
    return section
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .trim()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getSectionPreview = (section: string): string => {
    if (!queryResult?.areas?.[section]) return '';
    const area = queryResult.areas[section];
    const summary = area.summary || area.analysis || area.content || '';
    return summary.length > 150 ? summary.substring(0, 150) + '...' : summary;
  };

  return (
    <div className="pdf-export-overlay">
      <div className="pdf-export-dialog">
        <div className="pdf-export-header">
          <h2>Export PDF Report</h2>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="pdf-export-body">
          <div className="export-section">
            <h3>Select Sections to Export</h3>
            
            <div className="section-controls">
              <label className="checkbox-label select-all">
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={handleSelectAll}
                />
                <span>Select All Sections</span>
              </label>
            </div>

            <div className="sections-grid">
              {availableSections.map(section => (
                <div key={section} className="section-item">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={selectedSections.includes(section)}
                      onChange={() => handleSectionToggle(section)}
                    />
                    <div className="section-info">
                      <span className="section-name">{getSectionDisplayName(section)}</span>
                      <span className="section-preview">{getSectionPreview(section)}</span>
                    </div>
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className="export-section">
            <h3>Additional Options</h3>
            
            <div className="options-list">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={includeCharts}
                  onChange={(e) => setIncludeCharts(e.target.checked)}
                />
                <span>Include Charts & Visualizations</span>
              </label>

              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={includeMetadata}
                  onChange={(e) => setIncludeMetadata(e.target.checked)}
                />
                <span>Include Chart Sources & Insights</span>
              </label>

              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={includeRecommendations}
                  onChange={(e) => setIncludeRecommendations(e.target.checked)}
                />
                <span>Include Recommendations</span>
              </label>

              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={includeNextSteps}
                  onChange={(e) => setIncludeNextSteps(e.target.checked)}
                />
                <span>Include Next Steps</span>
              </label>
            </div>
          </div>

          <div className="export-preview">
            <h3>Export Preview</h3>
            <div className="preview-info">
              <p><strong>Selected Sections:</strong> {selectedSections.length} of {availableSections.length}</p>
              <p><strong>Estimated Pages:</strong> {Math.ceil(selectedSections.length * 2 + (includeRecommendations ? 1 : 0) + (includeNextSteps ? 1 : 0))}</p>
              <p><strong>Format:</strong> PDF (A4)</p>
            </div>
          </div>
        </div>

        <div className="pdf-export-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button 
            className="btn btn-primary" 
            onClick={handleExport}
            disabled={selectedSections.length === 0}
          >
            Export PDF
          </button>
        </div>
      </div>
    </div>
  );
};

export default PDFExportDialog;